/** @jsxImportSource @emotion/react */
import {
  Button,
  Container,
  Icon,
  ProgressCircle,
  Text,
  useToast,
} from "@coreai/component-library";
import { css, useTheme } from "@emotion/react";
import React, { useEffect, useState, useRef } from "react";
import { useLanguage } from "../../../../LanguageContext";
import {
  AssetModel,
  createAsset,
  triggerAssetDescription,
} from "../../../../services/assetsService";
import ThumbnailModal from "./ThumbnailModal";
import ImagePreview from "./ImagePreview";
import Markdown from "react-markdown";
import RateFeedback from "../../../CommonUi/RateFeedback/RateFeedback";
import { TFeedbackDetails } from "../../../../services/feedbackService";
import VBars from "../../../CommonUi/Loaders/VBars";
import EditDescriptionModal from "./EditDescriptionModal";
import img_fileType from "../../../../assets/img_fileType.svg";
import pdf_fileType from "../../../../assets/pdf_fileType.svg";
import video_fileType from "../../../../assets/video_fileType.svg";
import MediaPreview from "components/CommonUi/MediaPreview/MediaPreview";
import videoOver from "../../../../assets/videoOver.svg";


interface FileInputProps {
  setAsset: (asset: AssetModel | null) => void;
  rulesetId: string | null;
  setIsLoading: (isLoading: boolean) => void;
  setReview: (review: string) => void;
  isLoading: boolean;
  review: string;
  fileData?: any;
  assetId?: string | null;
  feedbackData?: TFeedbackDetails | null;
  politicalRiskAssmnt?: string | null;
  setFileType?: (fileType: string | null) => void;
}

declare global {
  interface Window {
    isAnalysisDescFilled: boolean; 
  }
}

const CHARACTER_LIMIT = 250000;

export default function FileInput(props: Readonly<FileInputProps>) {
  const theme = useTheme();
  const { toast } = useToast();
  const { t, language } = useLanguage();
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [asset, setAsset] = useState<AssetModel | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [characterCount, setCharacterCount] = useState<number>(
    props.review?.length || 0
  );
  const replaceFileIdRef = useRef<string | null| undefined>(null);
  const [isEditDescOpen, setIsEditDescOpen] = useState<boolean>(false);
  const [assetResponse, setAssetResponse] = useState<any>({});

  const handleReviewChange = (value: string) => {
    // if (value.length <= CHARACTER_LIMIT) {
      props.setReview(value);
      setCharacterCount(value.length);
    // }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  
    const droppedFile = e.dataTransfer.files?.[0] ?? null;
  
    if (!droppedFile) return;
  
    const allowedTypes = ["image/png", "image/jpeg", "application/pdf", "video/mp4"];
  
    const isValidType = allowedTypes.some((type) =>
      droppedFile.type.startsWith(type)
    );
  
    if (!isValidType) {
      let _msg = t("form_invalidFileTypeMsg");
      toast({
        variant: "warning",
        title: props.politicalRiskAssmnt? _msg.split('.')?.[0] || _msg : _msg,
      });
      e.dataTransfer.clearData();
      return;
    }
  
    props?.setFileType?.(droppedFile?.type ?? null);
    
    const maxSizeImage = 20 * 1024 * 1024; // 20 MB
    const maxSizeVideo = 300 * 1024 * 1024; // 300 MB
    const maxSizePdf = 30 * 1024 * 1024; // 30 MB
  
    const isTooLarge =
      (droppedFile.type.startsWith("image/") && droppedFile.size > maxSizeImage) ||
      (droppedFile.type.startsWith("video/mp4") && droppedFile.size > maxSizeVideo) ||
      (droppedFile.type === "application/pdf" && droppedFile.size > maxSizePdf);
  
    if (isTooLarge) {
      toast({
        variant: "warning",
        title: t("form_fileSizeMsg"),
      });
      e.dataTransfer.clearData();
      return;
    }
  
    setFile(droppedFile);
    generateImagePreview(droppedFile);
    props.setReview("");
    setCharacterCount(0);
  
    try {
      props.setIsLoading(true);
  
      const response = await createAsset({
        file: droppedFile,
        replaceFileId: replaceFileIdRef.current,
      });
  
      const responseAsset: AssetModel = {
        file: droppedFile,
        id: response.id,
      };
  
      setAsset(responseAsset);
    } catch (error) {
      console.error("Error creating asset:", error);
      toast({
        variant: "warning",
        title: t("form_assetErrorMsg"),
      });
      setFile(null);
      setImagePreview(null);
    } finally {
      props.setIsLoading(false);
      e.dataTransfer.clearData();
    }
  };

  const handleFileInputClick = () => {
    replaceFileIdRef.current = props?.assetId;
    document.getElementById("file-input")?.click();
  };

  const handleFileInputChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const selectedFile = e.target.files?.[0] ?? null;
    const allowedTypes = ["image/png", "image/jpeg", "application/pdf", "video/mp4"];

    const isValidType = allowedTypes.some((type) =>
      selectedFile?.type.startsWith(type)
    );
  
    if (!selectedFile || !isValidType) {
      let _msg = t("form_invalidFileTypeMsg");
      toast({
        variant: "warning",
        title: props.politicalRiskAssmnt? _msg.split('.')?.[0] || _msg : _msg,
      });
      e.target.value = "";
      return;
    }
    props?.setFileType?.(selectedFile?.type ?? null);

    const maxSizeImage = 20 * 1024 * 1024; // 20 MB
    const maxSizeVideo = 300 * 1024 * 1024; // 300 MB
    const maxSizePdf = 30 * 1024 * 1024; // 30 MB
  
    const isTooLarge =
      (selectedFile.type.startsWith("image/") && selectedFile.size > maxSizeImage) ||
      (selectedFile.type.startsWith("video/mp4") && selectedFile.size > maxSizeVideo) ||
      (selectedFile.type === "application/pdf" && selectedFile.size > maxSizePdf);
  
    if (isTooLarge) {
      toast({
        variant: "warning",
        title: t("form_fileSizeMsg"),
      });
      e.target.value = "";
      return;
    }
  
    setFile(selectedFile);
    generateImagePreview(selectedFile);
    props.setReview("");
    setCharacterCount(0);
  
    try {
      props.setIsLoading(true);
  
      const response = await createAsset({
        file: selectedFile,
        replaceFileId: replaceFileIdRef.current,
      });
  
      const responseAsset: AssetModel = {
        file: selectedFile,
        id: response.id,
      };
  
      setAsset(responseAsset);
    } catch (error) {
      console.error("Error creating asset:", error);
      toast({
        variant: "warning",
        title: t("form_assetErrorMsg"),
      });
      setFile(null);
      setImagePreview(null);
    } finally {
      props.setIsLoading(false);
    }
  };


  const generateImagePreview = (file: File) => {
    if (file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setImagePreview(null);
    }
  };

  function getDescription(asset_id: any, lang_code: string = "en") {
    if (asset && props.rulesetId) {
      props.setIsLoading(true);
      triggerAssetDescription(
        asset_id,
        props.rulesetId,
        lang_code
      ).then((response) => {
        if (response.success) {
          props.setReview(response.asset.description);
          setImagePreview(response.asset.thumbnail_urls[0]);
          setCharacterCount(response.asset?.description?.length);
          setAssetResponse(response?.asset);
        }
        props.setIsLoading(false);
      }).catch((err) => {
        toast({
          variant: "warning",
          title: t("form_assetErrorMsg"),
        });
        setFile(null);
        setImagePreview(null);
      }).finally(() => {
        props.setIsLoading(false);
      });
    }
  }
  useEffect(() => {
    props.setAsset(asset);
    if (asset?.id) {
      getDescription(asset.id, language);
    }
  }, [asset, language]);

  useEffect(() => {  

    if (props.fileData && "name" in props.fileData && "type" in props.fileData) {
      const file = new File([JSON.stringify(props.fileData)], props.fileData.name, { type: props.fileData.type });
      setFile(file);      
      if(props.fileData.type.startsWith("image/")){
        setImagePreview(props.fileData?.url);
      }else{
        setImagePreview(null);
      }      
    }

    if (props.review.length >= 1){
      window.isAnalysisDescFilled = true;
    }
  }, [props.review]);

    const getFileTypeIcon = () => {
      switch (file?.type) {
        case "application/pdf":
          return <img src={pdf_fileType} alt={`${pdf_fileType} file icon`} />;
        case "video/mp4":
          return <img src={video_fileType} alt={`${img_fileType} file icon`} />;
        case "image/png":
        case "image/jpg":
        case "image/jpeg":
          return <img src={img_fileType} alt={`${img_fileType} file icon`} />;
        default:
          return <img src={img_fileType} alt={`${img_fileType} file icon`} />;
      }
    };
  
  const titleSection = css`
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-between;
    margin: 20px 0 8px;
  `;

  const editBttn = css`
    cursor: pointer;
    font-size: 16px;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 2px;
    color: ${theme.colors.primary};

    &>div{
      margin-top: -6px;
    }
  `;

  const fileUploadStyles = css`
    display: flex;
    flex-direction: column;
    justify-content: center;
    border: 1px solid ${theme.colors.strokeGray};
    padding: ${theme.spacing.xl};
    text-align: center;
    border-radius: ${theme.radius.sm};
    color: ${theme.colors.textMedium};

    &:hover {
      cursor: pointer;
      border-color: ${theme.colors.primary};
      background-color: ${theme.colors.background};
    }

    ${file &&
    `
      &:hover {
        cursor: default;
        border-color: ${theme.colors.strokeGray};
        background-color: transparent;
      }
    `}
  `;

  const containerStyle = css`
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border: 1px solid ${theme.colors.strokeGray};
    padding: 0 ${theme.spacing.lg};
    border-radius: ${theme.radius.sm};
    color: ${theme.colors.textMedium};
    background-color: ${theme.colors.background};
    box-sizing: border-box;
  `;

  const fileNameContentStyles = css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: ${theme.spacing.md};
    width: 100%;

    @media (max-width: 450px) {
      flex-direction: column;
    }
  `;

  const fileNameTextStyles = css`
    flex-grow: 1;
    text-align: left;
    display: flex;
    gap: 4px;
    align-items: center;
    @media (max-width: 450px) {
      text-align: center;
    }
  `;

  const replaceFileStyles = css`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: ${theme.spacing.sm} ${theme.spacing.md};
    border-radius: ${theme.radius.sm};
    border: 1px solid ${theme.colors.background};

    &:hover {
      cursor: pointer;
      border-color: ${theme.colors.primary};
    }

    @media (max-width: 450px) {
      flex-direction: column;
    }

    .itemWrapper{
      color: ${theme.colors.primary};
    }
  `;

  const replaceTextStyles = css`
    color: ${theme.colors.primary};
    font-weight: 600;
    line-height: 150%;
    text-align: center;
  `;

  const inputTextareaDescriptionStyles = css`
    fontFamily: ${theme.typography.bodyMedium.fontFamily},
    fontSize: ${theme.typography.bodyMedium.fontSize},
    fontWeight: ${theme.typography.bodyMedium.fontWeight},
    lineHeight: ${theme.typography.bodyMedium.lineHeight},
    padding: 16px 0;
    width: 100%;
    text-align: left;
  `;

  const bodyStyle = {
    fontFamily: theme.typography.bodyMedium.fontFamily,
    fontSize: theme.typography.bodyMedium.fontSize,
    fontWeight: theme.typography.bodyMedium.fontWeight,
    lineHeight: theme.typography.bodyMedium.lineHeight,
    color: theme.colors.textDark,
  } as React.CSSProperties;

  const bodySemiStyle = {
    fontFamily: theme.typography.body_semiBold.fontFamily,
    fontSize: theme.typography.body_semiBold.fontSize,
    fontWeight: theme.typography.body_semiBold.fontWeight,
    lineHeight: theme.typography.body_semiBold.lineHeight,
    color: theme.colors.textDark,
    paddingBottom: theme.spacing.sm,
  } as React.CSSProperties;

  const textareaStyles = css`
    color: ${theme.colors.textMedium};
    height: 110px;
    text-align: justify;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    overflow: auto;
    text-overflow: ellipsis;
    width: 100%;
    font-size: 16px;
    padding: 10px;
    box-sizing: border-box;
    border-radius: ${theme.radius.xs};
    border: 1px solid hsl(198, 11%, 71%);

    /* Scrollbar Styling */
    &::-webkit-scrollbar {
      width: 10px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background: hsl(198, 11%, 60%);
      border-radius: 10px;
    }

    &::-webkit-scrollbar-button{
      display: none;
    }

    &::-webkit-scrollbar-thumb:hover {
      background: hsl(198, 11%, 45%);
    }

    /* 🎯 Hide or recolor scrollbar buttons (top/down arrows) */
    &::-webkit-scrollbar-button {
      background: white; /* or transparent to hide completely */
      width: 0;
      height: 0;
      display: none;
    }

    /* Firefox */
    scrollbar-color: hsl(198, 11%, 60%) transparent;
  `;

  const loaderWrapperStyles = css`
  padding: 12px 0;
  `;

  const playbackWrapper = css`
    position: relative;
    max-height: 150px;
    width: 248px;
    border: 1px solid ${theme.colors.strokeGray};
    padding: ${theme.spacing.md};
    border-radius: ${theme.radius.sm};
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    cursor: pointer;
    overflow: hidden;
    margin-top: 20px;

    > div > img {
      width: 232px;
      height: 134px;
      object-fit: cover;
    }
  `;

  const youtubeImg = css`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 64px;
    height: 64px;
    cursor: pointer;
    opacity: 0.7;
  `;

  return (
    <>
      <input
        id="file-input"
        type="file"
        accept=".jpg, .jpeg, .png, .pdf, .mp4"
        // accept={props.politicalRiskAssmnt ? ".pdf" : ".jpg, .jpeg, .png, .pdf, .mp4"}
        style={{ display: "none" }}
        onChange={handleFileInputChange}
      />
      <Container
        variant="column"
        css={fileUploadStyles}
        onClick={file ? undefined : handleFileInputClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {file || props?.review ? (
          <>
            <Container css={containerStyle}>
              <div css={fileNameContentStyles}>
                <div css={fileNameTextStyles}>{getFileTypeIcon()}{file?.name}</div>
                <button
                  css={replaceFileStyles}
                  onClick={(e) => {
                    e.preventDefault();
                    handleFileInputClick();
                  }}
                >
                  <Icon name="refresh" size="md" className="itemWrapper" />
                  <div css={replaceTextStyles}>
                    {t("form_inputFileChangeFile")}
                  </div>
                </button>
              </div>
            </Container>
            {(assetResponse && Object.keys(assetResponse).length > 0) && (
              <div css={playbackWrapper}>
                <MediaPreview
                  imageUrl={assetResponse?.thumbnail_urls?.[0] ?? assetResponse?.original_url}
                  originalUrl={assetResponse?.original_url}
                  imgType={assetResponse?.mime_type}
                  t={t}
                />
                {file?.type === "video/mp4" && (<img src={videoOver} alt="Play" css={youtubeImg} />)}
              </div>
            )}

            {!props.isLoading ? (
              <>
                <RateFeedback
                  title={t("feedback_rate_description")}
                  feedbackPosition="Description"
                  id=""
                  feedbackDetails={props?.feedbackData || null}
                  entityType="asset_id"
                  entityId={asset?.id || props?.assetId || null}
                />
                <div css={titleSection}>
                  <div>{t("form_inputFileDesc")}</div>
                  <div
                    onClick={() => setIsEditDescOpen(true)}
                    css={editBttn}
                  >
                    <Icon name="edit-01" size="xs"/>
                    {t("form_inputFileDescEditBtn")}
                  </div>
                </div>
                <div id="desc" css={textareaStyles}>{props.review}</div>
              </>
            ) : (
              <div css={loaderWrapperStyles}>
                <VBars size="medium" />
                <Text weight="semibold">{t("analyse_fileInput_waiting")}</Text>
                <div>{t("analyse_fileInput_waiting_subText")}</div>
              </div>
            )}
          </>
        ) : (
          <div>
            <Icon name="upload-02" size="xl" />
            <div style={bodySemiStyle}>
              <div>{t("form_inputFileTitle")}</div>
            </div>
            <div style={bodyStyle}>
              <div>{t("form_inputFileCriterias")}</div>
            </div>
          </div>
        )}
      </Container>
      {isEditDescOpen && (
        <EditDescriptionModal
          setIsEditDescOpen={setIsEditDescOpen}
          value={props.review}
          characterCount={characterCount}
          setCharacterCount={setCharacterCount}
          setReview={props?.setReview}
          CHARACTER_LIMIT={CHARACTER_LIMIT}
        />
      )}
    </>
  );
}
